import React, { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { useAuth, IPackageDetails, regenerateJwtToken } from 'lib/context';
import {
  Layout,
  Header,
  ComparePackages,
  ModalUpgradeEditPackage,
  ModalDowngradeCancelPackage,
  ModalCantDowngradePackage,
} from '../components';
import { Dictionary, keyBy } from 'lodash';
import { ModalContactAM } from 'lib/components/modal/ModalContactAM';
import { getProductPackages } from 'lib/api';
import { AddCardModal } from 'app/pages/billing/components/AddCardModal';
import { successToast } from 'lib/components/toasts/success';
import { useHistory } from 'react-router';
import { theme } from 'lib/style';
import {
  PackageWithAdditionalInfo,
  packageAdditionalInfo,
  PackageName,
} from 'lib/const/PackageAdditionalInfo';
import { addOrUpdatePlanSubscription } from 'lib/api/paymentApi';
import { useToastError } from 'lib/hooks';
import { useSubscriptionQuery } from 'lib/api/billing/useSubscriptionQuery';

type ContainerProps = {
  hasFixedHeader: boolean;
};

const Container = styled.div<ContainerProps>`
  box-sizing: border-box;
  margin: 0 auto;
  padding: 66px 16px 33px;
  width: 1040px;
  min-height: calc(100vh - 116px);
  max-width: 100%;
  ${({ hasFixedHeader }) =>
    hasFixedHeader &&
    css`
      padding: 200px 16px 33px 16px;
    `}
`;

export const billingCycles = [
  {
    value: 'monthly',
    text: 'Monthly',
  },
  {
    value: 'annual',
    text: 'Annual',
  },
];

enum PackageModal {
  HIDE,
  UPGRADE,
  DOWNGRADE,
  CANCEL,
  EDIT,
  CANT_DOWNGRADE,
}

const DEFAULT_PACKAGE = PackageName.LEGACY;

export const UpgradeContainer = () => {
  const history = useHistory();
  const { userData, setJwt } = useAuth();

  const currentPackageId =
    userData?.user?.packageDetails?.id || DEFAULT_PACKAGE;

  const { data: subscription, isLoading: subscriptionLoading } =
    useSubscriptionQuery();
  const [userLoading, setUserLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState(billingCycles[0].value);
  const [contactSubject, setContactSubject] = useState('');
  const [packages, setPackages] = useState<PackageWithAdditionalInfo[]>();
  const [packagesDictionary, setPackagesDictionary] =
    useState<Dictionary<PackageWithAdditionalInfo>>();

  const [changeToPackageId, setChangeToPackageId] = useState(0);

  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(PackageModal.HIDE);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [fixedHeader, setFixedHeader] = useState(false);
  const ref = useRef(null as null | HTMLDivElement);
  const [packageQuantity, setPackageQuantity] = useState(1);
  const { showError } = useToastError();

  const fetchPackages = async () => {
    const res = await getProductPackages();
    if (res) {
      const packagesArray = res.map((p: IPackageDetails) => {
        return { ...packageAdditionalInfo[p.id], ...p };
      });
      setPackages(packagesArray);
      setPackagesDictionary(keyBy(packagesArray, 'id'));
    }
  };

  useEffect(() => {
    fetchPackages();
    if (currentPackageId === 4) {
      history.push({ pathname: '/billing' });
    }
  }, []);

  const handleScroll = () => {
    const layoutHeight = ref?.current?.clientHeight || 0;
    if (!layoutHeight) return;
    setFixedHeader(window.pageYOffset > layoutHeight + 343);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (subscriptionLoading || !packagesDictionary) {
    return null;
  }

  const canDowngrade = () => {
    const packageHasMultipleSeats =
      packagesDictionary[currentPackageId]?.multipleSeats;
    return !packageHasMultipleSeats || subscription?.totalActiveUsers === 1;
  };

  const onContactUs = (packageTitle: string) => {
    setContactSubject(`Upgrade your plan to ${packageTitle} ${billingCycle}`);
    setShowContactUsModal(true);
  };

  const onClickPackage = (newPackageId: number, cancel: boolean = false) => {
    setChangeToPackageId(newPackageId);
    let showModal = PackageModal.HIDE;
    if (newPackageId < currentPackageId) {
      showModal = canDowngrade()
        ? PackageModal.DOWNGRADE
        : PackageModal.CANT_DOWNGRADE;
    } else if (newPackageId > currentPackageId) {
      showModal = PackageModal.UPGRADE;
    } else if (newPackageId === currentPackageId && cancel) {
      showModal = PackageModal.CANCEL;
    } else if (newPackageId === currentPackageId && !cancel) {
      showModal = PackageModal.EDIT;
    }
    setShowPackageModal(showModal);
  };

  const onUpgradePackage = (packageId: number, quantity: number) => {
    setShowPackageModal(PackageModal.HIDE);
    setPackageQuantity(quantity);
    if (subscription?.card && Object.keys(subscription.card).length) {
      upgradePackage(packageId, quantity);
    } else {
      setShowAddCardModal(true);
    }
  };

  const upgradePackage = async (packageId: number, quantity: number) => {
    try {
      setUserLoading(true);
      const payload = {
        paymentId: subscription?.card.id,
        interval: billingCycle,
        quantity,
        packageId,
      };
      await addOrUpdatePlanSubscription(payload);
      await regenerateJwtToken({ setJwt });
      setUserLoading(false);
      const upgradeTo = packagesDictionary[changeToPackageId]?.id;
      let toast = {
        title: `Covideo ${packagesDictionary[changeToPackageId].title} is successfully activated!`,
      } as any;
      if (upgradeTo === PackageName.PRO) {
        toast = {
          ...toast,
          additionalInfo: `Check add-ons to boost your productivity.`,
          button: {
            text: 'See Add-ons',
            textColor: theme.palette.gray100,
            backgroundColor: theme.palette.primaryBlack3Percent,
            action: goToAddOns,
          },
        };
      }
      if (upgradeTo === PackageName.TEAMS) {
        toast = {
          ...toast,
          additionalInfo: `Create users to fill seats in your Teams plan.`,
          button: {
            text: 'Manage Users',
            textColor: theme.palette.white,
            backgroundColor: theme.palette.covideoOrange100,
            action: goToManageUsers,
          },
        };
      }
      successToast(toast);
      history.push({ pathname: '/billing' });
    } catch (error) {
      showError(error);
    }
  };

  const addCardAndPay = () => {
    //TODO: add card
  };

  const goToAddOns = () => {
    history.push('/profile/add-ons');
  };

  const goToManageUsers = () => {
    history.push('/users/list');
  };

  if (userLoading || !packagesDictionary) {
    return null;
  }

  return (
    <>
      <Container hasFixedHeader={fixedHeader}>
        <Header
          billingCycle={billingCycle}
          onChangeBillingCycle={setBillingCycle}
        />
        <div ref={ref}>
          <Layout
            packages={packages || []}
            billingCycle={billingCycle}
            onContactUs={onContactUs}
            onClickPackage={onClickPackage}
            currentPackage={currentPackageId}
          />
        </div>
        <ComparePackages
          fixedHeader={fixedHeader}
          packages={packages || []}
          billingCycle={billingCycle}
          onContactUs={onContactUs}
          onClickPackage={onClickPackage}
          currentPackage={currentPackageId}
        />
      </Container>
      {showContactUsModal && (
        <ModalContactAM
          handleModalClose={() => setShowContactUsModal(false)}
          subject={contactSubject}
          title='Contact Account Manager'
        />
      )}
      {[PackageModal.UPGRADE, PackageModal.EDIT].includes(showPackageModal) && (
        <ModalUpgradeEditPackage
          newPackageDetails={packagesDictionary[changeToPackageId]}
          currentPackageDetails={packagesDictionary[currentPackageId]}
          billingCycle={billingCycle}
          onChangeBillingCycle={setBillingCycle}
          handleModalClose={() => setShowPackageModal(PackageModal.HIDE)}
          onSubmit={onUpgradePackage}
        />
      )}
      {[PackageModal.DOWNGRADE, PackageModal.CANCEL].includes(
        showPackageModal
      ) && (
        <ModalDowngradeCancelPackage
          newPackageDetails={packagesDictionary[changeToPackageId]}
          currentPackageDetails={packagesDictionary[currentPackageId]}
          handleModalClose={() => setShowPackageModal(PackageModal.HIDE)}
        />
      )}
      {showPackageModal === PackageModal.CANT_DOWNGRADE && (
        <ModalCantDowngradePackage
          newPackageDetails={packagesDictionary[changeToPackageId]}
          currentPackageDetails={packagesDictionary[currentPackageId]}
          handleModalClose={() => setShowPackageModal(PackageModal.HIDE)}
        />
      )}
      {showAddCardModal && (
        <AddCardModal
          handleModalClose={() => {
            setShowAddCardModal(false);
          }}
          onSubmit={() => addCardAndPay()}
          billingCycle={billingCycle}
          packageId={changeToPackageId}
          packageQuantity={packageQuantity}
          packagesDictionary={packagesDictionary}
        />
      )}
    </>
  );
};
